import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';
import type { GanadoEnCorral } from '../../api-client/ganado/ganado';
import AppDialog from '../../components/ui/AppDialog';
import AppTextField from '../../components/ui/AppTextField';
import { useGanadoStore } from '../../stores/ganado/ganado.store';
import { useUIStore } from '../../stores/ui/ui.store';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import EncabezadoDialogo from './EncabezadoDialogo';

interface PreñadaDialogoProps {
  open: boolean;
  onClose: () => void;
  animal: GanadoEnCorral;
}

const PreñadaDialogo = ({ open, onClose, animal }: PreñadaDialogoProps) => {
  const { loading } = useUIStore();
  const { ponerPreñada } = useGanadoStore();
  const [showConfirm, setShowConfirm] = useState(false);
  const form = useFormik({
    initialValues: {
      fechaTacto: dayjs(new Date()),
      fechaParto: dayjs(new Date()),
      mesesGestacion: 0,
      notas: ''
    },
    validationSchema: yup.object({
      fechaTacto: yup.date().required('La fecha de tacto es requerida'),
      fechaParto: yup.date().required('La fecha de parto es requerida'),
      mesesGestacion: yup
        .number()
        .integer('Debe ser un número entero')
        .typeError('Debe ser un número')
        .required('Es requerido')
        .min(1, 'No puede ser cero o negativo'),
      notas: yup.string().optional().max(100, 'Las notas no pueden exceder los 100 caracteres')
    }),
    onSubmit: async (values) => {
      await ponerPreñada({
        folioInterno: animal.folioInterno,
        partida: animal.partida,
        idCorral: animal.idCorral,
        fechaTacto: values.fechaTacto.toDate(),
        fechaParto: values.fechaParto.toDate(),
        mesesGestacion: values.mesesGestacion,
        notas: values.notas
      });

      onClose();
    }
  });

  const confirm = () => {
    form.submitForm();
    if (!form.isValid) return;
    setShowConfirm(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>PREÑADA</DialogTitle>
        <DialogContent>
          <form noValidate autoComplete="off" onSubmit={form.submitForm}>
            <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
              <EncabezadoDialogo animal={animal} />
              <Grid size={6}>
                <Typography>Fecha tacto</Typography>
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label=""
                  value={form.values.fechaTacto}
                  onChange={(date) => form.setFieldValue('fechaTacto', date)}
                  disabled={loading}
                />
              </Grid>
              <Grid size={6}>
                <Typography>Fecha parto (aproximada)</Typography>
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label=""
                  value={form.values.fechaParto}
                  onChange={(date) => form.setFieldValue('fechaParto', date)}
                  disabled={loading}
                />
              </Grid>
              <Grid size={6}>
                <Typography>Meses gestación</Typography>
              </Grid>
              <Grid size={6}>
                <AppTextField
                  name="mesesGestacion"
                  label=""
                  value={form.values.mesesGestacion}
                  disabled={loading}
                  maxLength={2}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.mesesGestacion && Boolean(form.errors.mesesGestacion)}
                  helperText={form.touched.mesesGestacion ? form.errors.mesesGestacion : undefined}
                />
              </Grid>
              <Grid size={12}>
                <AppTextField
                  name="notas"
                  label="Notas"
                  value={form.values.notas}
                  rows={3}
                  disabled={loading}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.notas && Boolean(form.errors.notas)}
                  maxLength={100}
                  helperText={getLengthOrErrorText(
                    form.touched.notas ? form.errors.notas : '',
                    form.values.notas.length,
                    100
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            onClick={() => setShowConfirm(true)}
            disabled={loading}
            startIcon={<CheckCircleIcon />}
            disableElevation
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            startIcon={<ExitToAppIcon />}
            onClick={onClose}
            disabled={loading}
            disableElevation
          >
            Salir
          </Button>
        </DialogActions>
      </Dialog>
      <AppDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Confirme"
        message="¿Está seguro de guardar y registrar el movimiento?"
        buttons={
          <>
            <Button color="secondary" variant="contained" disableElevation onClick={confirm}>
              Sí
            </Button>
            <Button variant="contained" disableElevation onClick={() => setShowConfirm(false)}>
              No
            </Button>
          </>
        }
      />
    </>
  );
};

export default PreñadaDialogo;
